import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';
import {ColumnBase, SelectableSettings, TreeListComponent} from '@progress/kendo-angular-treelist';
import {NODE_SF_TYPES} from '@q9elements/ui-kit/refmodels';
import * as _ from 'lodash';
import {Observable, of} from 'rxjs';
import {catchError, filter, map, take} from 'rxjs/operators';

import {
  AttachmentObject,
  constants,
  NodePath,
  OrgInfo,
  REF_MODEL_STORE,
  Refmodel,
  RefModelNode,
  RefModelStoreService,
  SalesForceData,
  TakeUntilComponentDestroy,
  User
} from '@q9elements/ui-kit/common';

import {Q9ContextMenuComponent, TDataItem} from '@q9elements/ui-kit/components';
import {RefModelSidebarService} from '@q9elements/ui-kit/refmodels';
import {RmeNodeDaoAbstractService} from '../../../core/abstract/rme-node-dao-abstract.service';
import {IGridColumn} from '../../../custom-grid/models/grid-column.interface';
import {GridSelectorService} from '../../../custom-grid/services/grid-selector.service';
import {TreeNode} from '../../../models/rme/tree-node';
import {TreeUpdateObject, UpdateTreeChildren} from '../../../models/rme/tree-update-object';
import {TreeStoreItemsEnum} from '../../../new-context-menu/models/context-menu.enum';
import {ContextMenuActionsService} from '../../../new-context-menu/services/context-menu-actions.service';
import {ContextMenuViewService} from '../../../new-context-menu/services/context-menu-view.service';
import {AttachmentsService} from '../../../services/dao/attachments.service';
import {UpdateTreeService} from '../../../services/update-tree.service';
import {NewNodeTypes} from '../../models/enums/NodeTypes.enum';
import {TreeListViewEnum} from '../../models/enums/TreeListView.enum';
import {ITreeNode} from '../../models/grid-node.interface';
import {ColumnsGenerateService} from '../../services/columns-generate.service';
import {EditorInterfaceDialogService} from '../../services/editor-interface-dialog.service';
import {GridNodeFormatterService} from '../../services/grid-node-formatter.service';
import {NodeRemoveEmitterService} from '../../services/node-remove-emitter.service';
import {GridContextService} from '../../services/strategies/grid-context.service';
import {TreePaginationService} from '../../services/tree-pagination.service';
import {TreeGridTagsColumn} from './enums/tree-grid-context-menu.enum';

const AVERAGE_TAG_WIDTH = 90;

@Component({
  selector: 'app-node-tree-grid',
  templateUrl: './node-tree-grid.component.html',
  styleUrls: ['./node-tree-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NodeTreeGridComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild('treeList') public treeList: TreeListComponent;
  @ViewChild('contextMenu') public contextMenu: Q9ContextMenuComponent;

  @Output() selectRow: EventEmitter<any> = new EventEmitter();
  @Output() removeNode: EventEmitter<any> = new EventEmitter();
  @Output() makeNodeRoot: EventEmitter<any> = new EventEmitter();
  @Output() showMaxNodesDOMWarn: EventEmitter<any> = new EventEmitter();

  @Input() view: TreeListViewEnum;
  @Input() sfTypeFilter: string;

  public loading$: Observable<boolean> = this.treePaginationService.selectRootNodesLoading();
  public selectedRows = [];
  public expandedKeys = [];
  public selectableConfig = {
    mode: 'row'
  } as SelectableSettings;
  public nodePath$: Observable<NodePath[]> = this.rmeStore.select(REF_MODEL_STORE.NODE_PATH);
  public refModel: Refmodel = this.rmeStore.refModel;
  public user: Partial<User> = this.rmeStore.user;
  public orgInfo: OrgInfo;
  public selectedNode: RefModelNode;
  public attachmentsObj: AttachmentObject;
  public gdprStatuses: any;
  public isExternalRefModel: boolean = this.refModel.external;
  public rootData: SalesForceData;
  public sourceData$: any;
  public hideMenu: boolean;
  public isPreview: boolean;
  public menuItems = [];
  public columns: IGridColumn<ITreeNode>[] = [];
  public selectedGridNodes: TDataItem[];

  private selectedDataItem: any;
  private tagsColumnWidth: number;
  private packageNotInstalled = constants.PACKAGE_NOT_CONNECTED;
  private nodeService: RmeNodeDaoAbstractService = inject(RmeNodeDaoAbstractService);
  private updateTreeService: UpdateTreeService = inject(UpdateTreeService);
  private attachmentsService: AttachmentsService = inject(AttachmentsService);
  private injector: Injector = inject(Injector);

  constructor(
    public translate: TranslateService,
    private readonly gridContext: GridContextService,
    private rmeStore: RefModelStoreService,
    private treePaginationService: TreePaginationService,
    private dialogService: EditorInterfaceDialogService,
    private changeDetectorRef: ChangeDetectorRef,
    private contextMenuService: ContextMenuActionsService,
    private contextMenuViewService: ContextMenuViewService,
    private columnsGenerateService: ColumnsGenerateService,
    private gridSelectorService: GridSelectorService,
    private nodeFormatterService: GridNodeFormatterService,
    private nodeRemoveEmitterService: NodeRemoveEmitterService,
    private ngZone: NgZone,
    private refModelSidebarService: RefModelSidebarService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initGridStrategy();
    this.initGDPRStatuses();

    this.initTreeGridSelectionChanges();

    this.isPreview = !this.rmeStore.isAuthor;

    // subscriptions to store
    this.initOrgInfoStoreSubscription();
    this.initSelectedNodeStoreSubscription();
    this.initSelectedNodeAttachmentsStoreSubscription();
    this.initUpdateNodeInTreeSubscription();
    this.initUpdateChildrenNodesInTreeSubscription();
    this.initRootDataSubscription();
    this.initRefModelStoreSubscription();
    this.initNodeRemoveEmits();

    // subscriptions to context menu outputs
    this.subscribeToUpdateNodeGdprStatusInTreeEvent();
    this.subscribeToProposedNodeCreatingDialogEmit();
    this.subscribeToAddNewNodeEvent();
    this.getTreeListColumns();
    this.setUpEffect();
    this.initRefModelNodes();
  }

  setUpEffect() {
    effect(
      () => {
        if (this.refModelSidebarService.reloadNodeTreeData()) {
          this.getDataSource();
          this.refModelSidebarService.reloadNodeTreeData.set(false);
        }
      },
      {allowSignalWrites: true, injector: this.injector}
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isFilterInChanges = this.isFilterInChanges(changes);

    if (isFilterInChanges) {
      this.gridContext.setStrategy(this.view);
      this.treePaginationService.setPageIndex(0);
      const rootNode = this.nodeFormatterService.formatNode(this.rootData, true);

      if (isFilterInChanges) {
        this.treePaginationService.setRootNodesLoading(true);
        this.getDataSource([rootNode]);
        this.checkColumns();
      }
    }
  }

  ngAfterViewInit() {
    this.getTreeListColumns();
    this.treeList.contentScroll
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(() => this.contextMenu.isOpened)
      )
      .subscribe(() => {
        this.contextMenu.closeMenu();
        this.changeDetectorRef.detectChanges();
      });

    this.checkColumns();
  }

  ngOnDestroy(): void {
    this.contextMenuService.resetEmitters();
    this.gridSelectorService.resetSelectedKeys();
  }

  private initRefModelNodes() {
    this.nodeService
      .getNodesByParent(this.refModel._id, this.refModel._id, {
        skip: 0,
        limit: constants.pagination.nodesPerPageDefault
      })
      .pipe(take(1))
      .subscribe(res => {
        this.rmeStore.set(REF_MODEL_STORE.REF_MODEL_NODES, res.nodes || []);
      });
  }

  private initGridStrategy(): void {
    this.gridContext.setStrategy(this.view);
  }

  private initGDPRStatuses(): void {
    this.gdprStatuses = {};
    _.map(constants.gdprOptions.gdprStatuses, item => {
      this.gdprStatuses[item.key] = item.value;
    });
  }

  // Subscriptions to stores
  private initOrgInfoStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.ORG_INFO)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((orgInfo: OrgInfo) => (this.orgInfo = {...orgInfo}));
  }

  private initSelectedNodeStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(
          (selected: RefModelNode) => !this.selectedNode || selected._id !== this.selectedNode._id
        )
      )
      .subscribe((selectedNode: RefModelNode) => this.handleNewSelectedNode(selectedNode));
  }

  private initSelectedNodeAttachmentsStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE_ATTACHMENTS)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe((attachmentsObj: AttachmentObject) => {
        this.attachmentsObj = attachmentsObj;

        if (this.selectedNode._id !== this.refModel._id && !attachmentsObj.loading) {
          const [dataItem, parentItem] = this.getNodeAndParentFromTree(this.selectedNode._id);
          const links = this.attachmentsService.getLinksNumber(attachmentsObj);

          if (dataItem && parentItem && dataItem?.data?.links !== links) {
            this.updateNodeInTree(dataItem.id, parentItem.id, 'links', links);
          }
        }
      });
  }

  private initUpdateNodeInTreeSubscription(): void {
    this.updateTreeService
      .select(TreeStoreItemsEnum.UPDATE_NODE_IN_TREE)
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(data => !!data && !!this.treeList)
      )
      .subscribe((data: TreeUpdateObject) => {
        const {nodeId, property} = data;
        const [dataItem, parentItem] = this.getNodeAndParentFromTree(nodeId);

        if (property === 'gdprStatus' && data.value) {
          data.value = this.translate.instant(this.gdprStatuses[data.value]);
        }

        if (
          property === TreeGridTagsColumn.field &&
          !_.isEqual(dataItem?.data[property], data.value)
        ) {
          this.updateTagsColumnWidth([{tags: data.value}]);
        }

        if (dataItem && parentItem && !_.isEqual(dataItem?.data[property], data.value)) {
          this.updateNodeInTree(dataItem.id, parentItem.id, property, data.value);
        }

        if (dataItem && !parentItem && !_.isEqual(dataItem?.data[property], data.value)) {
          this.updateRootNode(dataItem.id, property, data.value);
        }
      });
  }

  private initUpdateChildrenNodesInTreeSubscription(): void {
    this.updateTreeService
      .select(TreeStoreItemsEnum.UPDATE_CHILDREN_NODES_IN_TREE)
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((data: UpdateTreeChildren) => this.updateChildrenNodesInTree(data));
  }

  private initRootDataSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.ROOT_DATA)
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(data => data && data !== this.rootData)
      )
      .subscribe((data: any) => {
        const rootNode = this.nodeFormatterService.formatNode(data, true);
        this.expandedKeys = [];
        this.selectedRows = [];
        this.treePaginationService.setRootNodesLoading(true);
        this.getDataSource([rootNode]);
        this.rootData = Object.assign({}, data);
        this.selectRow.emit(rootNode);

        if (this.treeList) {
          this.checkColumns();
        }
      });
  }

  private initRefModelStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((data: Refmodel) => (this.refModel = data));
  }

  private initTreeGridSelectionChanges() {
    this.gridSelectorService.selectedKeys$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(items => !!items.length)
      )
      .subscribe((items: TreeNode[]) => this.onSelectionChange(items));
  }

  private initNodeRemoveEmits() {
    this.nodeRemoveEmitterService
      .subscribeToEmitter()
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(nodeData => this.removeNodeFromTree(nodeData));
  }

  private subscribeToUpdateNodeGdprStatusInTreeEvent() {
    this.contextMenuService.updateNodeGdprStatusInTreeEmit$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(nodes => !!nodes.length)
      )
      .subscribe((nodes: TreeNode[]) => {
        nodes.forEach(node => {
          this.updateNodeInTree(
            node.id,
            node.parentId,
            'gdprStatus',
            this.translate.instant(this.gdprStatuses['na'])
          );
        });
      });
  }

  private subscribeToProposedNodeCreatingDialogEmit() {
    this.contextMenuService.proposedNodeCreatingDialogEmit$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(dialogData => !!Object.keys(dialogData).length)
      )
      .subscribe(dialogData => {
        this.dialogService
          .createProposedNodeDialog(dialogData)
          .afterClosed()
          .pipe(filter(Boolean))
          .subscribe((nodes: RefModelNode[]) =>
            nodes.forEach(node => {
              this.checkVisibilityAndAddNewNodeToTree(
                NewNodeTypes.CHILD,
                node,
                dialogData.parentId
              );
            })
          );
      });
  }

  private subscribeToAddNewNodeEvent() {
    this.contextMenuService.addNewNodeEmit$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(data => !!Object.keys(data).length)
      )
      .subscribe(({type, node, parentId}) =>
        this.checkVisibilityAndAddNewNodeToTree(type, node, parentId)
      );
  }

  private handleNewSelectedNode(node: RefModelNode) {
    this.selectedNode = Object.assign({}, node);

    this.hideMenu = this.hideContextMenu();
  }

  private isFilterInChanges(changes: SimpleChanges): boolean {
    const filterChanges = changes.sfTypeFilter;
    return Boolean(
      filterChanges && filterChanges.currentValue !== filterChanges.previousValue && this.rootData
    );
  }

  private hideContextMenu(): boolean {
    return; /*(
      (this.isPreview || this.refModel.external) && this.selectedNode._id === this.refModel._id
    );*/
  }

  private updateNodeInTree(nodeId: string, parentNodeId: string, key: string, value: any): void {
    if (this.treeList) {
      const sourceData = this.treeList.view.loaded as Map<string, TreeNode[]>;
      const parentNodeChildren = sourceData.get(parentNodeId);
      if (parentNodeChildren?.length) {
        const targetNodeIndex = parentNodeChildren.findIndex(node => node.id === nodeId);
        const targetNode = {...parentNodeChildren[targetNodeIndex]};
        targetNode[key] = value;
        parentNodeChildren[targetNodeIndex] = targetNode;
        this.treeList.updateView();
      }
    }
  }

  private updateRootNode(nodeId: string, key: string, value: any) {
    const data = this.treeList.data as any[];
    const rootNodeIndex = data.findIndex(node => node.id === nodeId);

    if (rootNodeIndex !== -1) {
      const rootNode = data[rootNodeIndex];
      rootNode[key] = value;
      data[rootNodeIndex] = Object.assign({}, rootNode);
      this.treeList.updateView();
    }
  }

  private updateChildrenNodesInTree(data: UpdateTreeChildren) {
    const sourceData = this.treeList.view.loaded as Map<string, TreeNode[]>;
    const children = sourceData.get(data.node._id);
    const status = data.value ? this.translate.instant(this.gdprStatuses['unassessed']) : '';

    if (children.length) {
      _.forEach(children, child => {
        const oldStatus = _.camelCase(child['gdprStatus']);
        if (status && oldStatus && status !== oldStatus) {
          return;
        }
        child.gdprStatus = status;
      });
    }

    this.treeList.updateView();
  }

  public getDataSource(data?: any[]) {
    const nodes = data ? data : [this.nodeFormatterService.formatNode(this.rootData, true)];

    this.sourceData$ = of(nodes);
    const currentSelection = this.selectedDataItem;

    if (data) {
      this.selectNode(nodes[0]);
    } else if (currentSelection) {
      this.selectNode(currentSelection);
    } else {
      this.selectNode(nodes[0]);
    }

    if (nodes[0] && nodes[0].nodes.length) {
      this.expandFirstRow(nodes[0]);
    } else {
      this.treePaginationService.setRootNodesLoading(false);
    }
  }

  public fetchChildNodes = (item: any): Observable<TreeNode[]> => {
    const options = {data: {id: item.id, typeFilter: this.sfTypeFilter}};

    return this.readTreeListDataSource(options, this.refModel._id, item);
  };

  public hasChildren(item: any) {
    return item.hasChildren;
  }

  private readTreeListDataSource(
    options: any,
    refModelId: any,
    rootData: any
  ): Observable<TreeNode[]> {
    if (!options.data || !options.data.id) {
      this.treePaginationService.setRootNodesLoading(true);

      return of(rootData);
    }

    _.set(options, 'data.typeFilter', this.sfTypeFilter);

    let query;
    const root = this.rmeStore.rootData;

    if (root._id === options.data.id) {
      /** Load root nodes */
      this.treePaginationService.setRootNodesLoading(true);
      query = {
        skip: this.treePaginationService.pageIndex * this.treePaginationService.nodesPerPage,
        limit: this.treePaginationService.nodesPerPage
      };
    } else {
      query = {
        skip: constants.pagination.skipDefault,
        limit: constants.pagination.nodesPerPageDefault
      };
    }

    return this.gridContext
      .execute()
      .getItems({
        rmId: refModelId,
        options,
        query,
        isSf: Boolean(this.refModel.sf)
      })
      .pipe(
        map((res: any) => {
          const allChildrenLength = res.length || 0;
          const loadedNodes = res.nodes || [];

          if (!loadedNodes.length) {
            this.treePaginationService.setRootNodesLoading(false);
            return [];
          } else {
            this.showMaxChildLoadedWarning(root._id, options.data.id, allChildrenLength);
            this.checkAllNodesNumberInDOM(loadedNodes.length);

            if (root._id === options.data.id) {
              this.treePaginationService.setRootNodesLength(allChildrenLength || 0);
            }

            /** Modify nodes before inserting in tree list */
            const nodesArr = loadedNodes.map(node => this.nodeFormatterService.formatNode(node));
            this.treePaginationService.setRootNodesLoading(false);
            this.updateTagsColumnWidth(nodesArr);

            return nodesArr;
          }
        }),
        catchError(error => {
          this.treePaginationService.setRootNodesLoading(false);
          console.error('Failed to get child nodes: ', error);

          return of([]);
        })
      );
  }

  /** Tree list columns */
  public getTreeListColumns() {
    this.columns = this.columnsGenerateService.generateTreeGridColumns(
      this.orgInfo,
      this.refModel,
      this.tagsColumnWidth
    );
  }

  private checkColumns(): void {
    const gdprColumnIndex = 3;
    let fieldImpactColumnIndex = 5;

    const columns = this.treeList.columns.toArray();

    /** Check GDPR column */
    if (this.refModel && this.refModel.gdprEnabled && columns[gdprColumnIndex].hidden) {
      fieldImpactColumnIndex = 6;
      columns[gdprColumnIndex].hidden = false;
    } else if (this.refModel && !this.refModel.gdprEnabled && !columns[gdprColumnIndex].hidden) {
      fieldImpactColumnIndex = 5;
      columns[gdprColumnIndex].hidden = true;
    }

    /** Check FieldImpact column */
    if (
      this.isExternalRefModel &&
      this.refModel &&
      this.orgInfo &&
      this.orgInfo.packageVersion !== this.packageNotInstalled &&
      columns[fieldImpactColumnIndex].hidden
    ) {
      columns[fieldImpactColumnIndex].hidden = false;
    } else if (
      this.isExternalRefModel &&
      this.refModel &&
      this.orgInfo &&
      this.orgInfo.packageVersion === this.packageNotInstalled &&
      !columns[fieldImpactColumnIndex].hidden
    ) {
      columns[fieldImpactColumnIndex].hidden = true;
    }
    this.treeList.updateView();
  }

  private expandFirstRow({id}) {
    this.expandedKeys.push(id);
    this.selectedRows.push({itemKey: id});
  }

  public onCellClick($event) {
    if ($event.type === 'contextmenu') {
      const {originalEvent, dataItem} = $event;
      originalEvent.preventDefault();
      const nodeId = dataItem.id;
      const isSameNode = this.selectedNode?._id === nodeId;

      this.selectNode(dataItem);

      if (isSameNode) {
        this.hideMenu = this.hideContextMenu();
        if (!this.hideMenu) {
          this.menuItems = this.getContextMenuItems();
          this.contextMenu.openMenu(originalEvent);
        }
      } else {
        this.rmeStore
          .select<RefModelNode>(REF_MODEL_STORE.SELECTED_NODE)
          .pipe(
            this.takeUntilComponentDestroy(),
            filter(node => node?._id === nodeId),
            take(1)
          )
          .subscribe(node => {
            this.selectedNode = Object.assign({}, node);
            this.hideMenu = this.hideContextMenu();
            if (!this.hideMenu) {
              this.menuItems = this.getContextMenuItems();
              this.changeDetectorRef.detectChanges();
              this.contextMenu.openMenu(originalEvent);
            }
          });
      }

      this.treeList.view.updateSelectedState();
    }
  }

  public handleSelectionChange($event) {
    this.gridSelectorService.handleKeySelectionChange($event.items.map(item => item.dataItem));
  }

  public onSelectionChange(items: TreeNode[]) {
    const [dataItem] = items;
    this.selectedGridNodes = items;

    this.emitSelectedNode(dataItem);
  }

  private selectNode(dataItem: ITreeNode): void {
    const selected = {items: [{dataItem}]};

    this.handleSelectionChange(selected);
  }

  public onExpandChange($event) {
    const {expand, dataItem} = $event;

    expand
      ? this.expandedKeys.push(dataItem.id)
      : (this.expandedKeys = this.expandedKeys.filter(key => key !== dataItem.id));

    this.selectNode(dataItem);
    this.updateTagsColumnWidth([dataItem]);
  }

  // tslint:disable-next-line:no-unused-expression
  public isSelected = (dataItem: any, column?: ColumnBase, columnIndex?: number) => {
    return this.selectedRows[0]?.itemKey === dataItem.id;
  };

  private emitSelectedNode(dataItem: any) {
    this.selectedDataItem = dataItem;
    this.selectedRows = [{itemKey: dataItem.id}];
    this.selectRow.emit(dataItem);
    this.treeList?.updateView();
  }

  public getContextMenuItems() {
    if (this.displayGroupingNodeContextMenu()) {
      return this.contextMenuViewService.getTreeGridContextMenuItemsForGroupingNodes();
    }

    return this.contextMenuViewService.getTreeGridContextMenuItems();
  }

  public collapseAllNodes() {
    this.treePaginationService.setRootNodesLoading(true);
    const nodes = this.treeList.view.data;

    for (const node of nodes) {
      node.expanded = false;
    }

    this.expandedKeys = [];
    this.treeList.updateView();
    this.treePaginationService.setRootNodesLoading(false);
  }

  public removeNodeFromTree(dataItem) {
    const node = {...this.treeList.view.data.find(item => item.data.id === dataItem.nodeId)};
    const parentItem = this.treeList.view.data.find(item => item.data.id === node.parent.id);

    this.updateStoreBeforeNodeRemoving({
      _id: dataItem.nodeId,
      parentId: parentItem.data.id
    } as RefModelNode);

    this.removeNodeFromParent(dataItem.nodeId, parentItem.data.id);
    this.updateParentNodeAfterChildDeleted(parentItem, dataItem.nodeId);

    const parentItemNodes = this.treeList.view.data.filter(
      treeNode => treeNode.data.parentId === parentItem.data.id
    );

    if (parentItemNodes.length) {
      const lastNode = this.selectLastChildFromParentNode(parentItem.data.id);
      this.selectNode(lastNode);
    } else {
      this.treeList.collapse(parentItem);
      this.selectNode(parentItem.data);
    }

    if (parentItem.data.id === this.rootData._id) {
      // Comment below from old component - need to check is it actual
      // Remove deleted node from Root data nodes array without trigger subscribers
      this.checkAndAddNodeFromNextPageAfterDelete();
      this.treePaginationService.setRootNodesLength(this.treePaginationService.rootNodesLength - 1);
    }

    this.checkAllNodesNumberInDOM();
  }

  private displayGroupingNodeContextMenu(): boolean {
    if (!this.isExternalRefModel || this.selectedNode._id === this.refModel._id) {
      return false;
    }

    const extraSupportedGroupingNodes: string[] = [
      NODE_SF_TYPES.OBJECT,
      NODE_SF_TYPES.NAMESPACE,
      NODE_SF_TYPES.DATA_CLOUD,
      NODE_SF_TYPES.AGENTFORCE
    ];
    const isFirstLevelGroupingNode = this.selectedNode.identity.split('.').length === 1;
    const isSecondLevelGroupingNode = extraSupportedGroupingNodes.includes(
      this.selectedNode.parentPath
    );
    const namespaceGroupingNodeLength = 3;
    const isManagedPackageGroupingNode =
      this.selectedNode.identity.startsWith(NODE_SF_TYPES.NAMESPACE) &&
      this.selectedNode.identity.split('.').length === namespaceGroupingNodeLength;

    return isFirstLevelGroupingNode || isSecondLevelGroupingNode || isManagedPackageGroupingNode;
  }

  private checkVisibilityAndAddNewNodeToTree(
    type: NewNodeTypes,
    newNode: RefModelNode,
    parentId: string
  ): void {
    if (!this.newNodeWillBeInView(parentId)) {
      const confirmDialogRef = this.dialogService.getGoToNewNodeDialog(newNode);

      confirmDialogRef
        .afterClosed()
        .pipe(filter(Boolean))
        .subscribe(() => {
          this.selectedNode = newNode;
          this.rmeStore.set(REF_MODEL_STORE.SELECTED_NODE, this.selectedNode);
          this.makeNodeRoot.emit({id: this.selectedNode._id});
        });
    } else {
      this.addNewNodeToTree(newNode, type);
    }

    this.addNewNodeToParentInStore(newNode);

    if (newNode.parentId === this.rootData._id || _.isNull(newNode.parentId)) {
      const rootNodesLength = this.treePaginationService.rootNodesLength + 1;
      this.treePaginationService.setRootNodesLength(rootNodesLength);
    }
  }

  private newNodeWillBeInView(parentId: string): boolean {
    if (parentId === this.rmeStore.rootData._id) {
      const nodesPerPage = this.treePaginationService.nodesPerPage;
      const nodesOnCurrentPage =
        this.treePaginationService.rootNodesLength -
        nodesPerPage * this.treePaginationService.pageIndex;

      return nodesOnCurrentPage < nodesPerPage;
    } else {
      const treeList = this.treeList.view.data;
      const parentRow = treeList.find(node => node.data.id === parentId);

      if (!parentRow) {
        return false;
      }

      return parentRow.data.nodes.length < constants.pagination.maxLoadedChildren;
    }
  }

  private addNewNodeToTree(node: any, type: string): void {
    const treeListData = this.treeList.view.data;
    node = this.nodeFormatterService.formatNode(node);
    const parentId = node.parentId ? node.parentId : this.refModel._id;
    const parentRowData = treeListData.find(treeNode => treeNode.data.id === parentId);
    const isChildrenLoaded = this.isChildrenLoaded(parentId);

    if (type === NewNodeTypes.CHILD) {
      if (parentRowData.hasChildren) {
        /** if children haven't expanded and doesn't contain loaded children
         * we will receive all children via server call after expand */
        if (isChildrenLoaded) {
          this.addNewNode(parentId, node);
        }
      } else {
        this.addNewNode(parentId, node);
      }

      this.updateParentNodeAfterChildAdded(parentRowData, node.id);

      const parentChildrenUnloaded =
        parentRowData.hasChildren && !parentRowData.expanded && !isChildrenLoaded;
      const firstChildAddedToParent =
        parentRowData.hasChildren &&
        !parentRowData.expanded &&
        !isChildrenLoaded &&
        parentRowData.data.nodes.length === 1;
      const parentChildrenLoaded = !parentRowData.expanded && isChildrenLoaded;

      if (parentRowData.expanded) {
        this.selectAndScrollToNode(node.id);
      }

      if (parentChildrenUnloaded) {
        this.treeList.view.childrenLoaded.pipe(take(1)).subscribe(() => {
          this.selectAndScrollToNode(node.id);
        });

        this.treeList.expand(parentRowData);
      }

      if (firstChildAddedToParent || parentChildrenLoaded) {
        this.treeList.expand(parentRowData);
        this.selectAndScrollToNode(node.id);
      }
    }

    if (type === NewNodeTypes.SIBLING) {
      this.addNewNode(parentId, node);
      this.selectAndScrollToNode(node.id);
    }

    if (parentRowData.gdprStatus && node.parentId) {
      this.updateNodeInTree(parentId, parentRowData.data.parentId, 'gdprStatus', '');
    }

    this.checkAllNodesNumberInDOM();
  }

  private addNewNodeToParentInStore(node: RefModelNode) {
    if (this.rmeStore.rootData._id === node.parentId || _.isNull(node.parentId)) {
      this.rmeStore.rootData.nodes.push(node._id);
      this.rootData.nodes.push(node._id);
    } else if (this.rmeStore.selectedNodeId === node.parentId) {
      this.rmeStore.selectedNode.nodes.push(node._id);
      this.selectedNode.nodes.push(node._id);
    }
  }

  private updateParentNodeAfterChildAdded(parentNode, nodeId) {
    const {nodes} = parentNode.data;
    parentNode.hasChildren = true;
    parentNode.data.hasChildren = true;

    if (!nodes.includes(nodeId)) {
      nodes.push(nodeId);
    }
  }

  private updateStoreBeforeNodeRemoving(node: RefModelNode) {
    const rootData = this.rmeStore.rootData;
    if (rootData._id === node.parentId || _.isNull(node.parentId)) {
      rootData.nodes = rootData.nodes.filter(id => id !== node._id);
      this.rootData.nodes = this.rootData.nodes.filter(id => id !== node._id);
    }

    if (this.rmeStore.selectedNodeId === node.parentId) {
      this.rmeStore.selectedNode.nodes = this.rmeStore.selectedNode.nodes.filter(
        id => id !== node._id
      );
      this.selectedNode.nodes = this.selectedNode.nodes.filter(id => id !== node._id);
    }
  }

  private updateParentNodeAfterChildDeleted(parentNode, nodeId) {
    parentNode.data.nodes = parentNode.data.nodes.filter(node => node !== nodeId);

    if (!parentNode.data.nodes.length) {
      parentNode.hasChildren = false;
      parentNode.data.hasChildren = false;
    }
  }

  private selectAndScrollToNode(nodeId: string) {
    const treeListData = this.treeList.view.data;
    const newNodeRowData = treeListData.find(node => node.data.id === nodeId);

    if (newNodeRowData) {
      setTimeout(() => {
        const newNode = this.treeList.ariaRoot.nativeElement.querySelector(
          `[data-treelist-view-index="${newNodeRowData.rowIndex}"]`
        );

        this.selectNode(newNodeRowData.data);
        this.treeList.view.updateSelectedState();
        newNode.scrollIntoView({behavior: 'smooth'});
      }, 200);
    }
  }

  private addNewNode(parentNodeId: string, node: TreeNode): void {
    const dataSource = this.treeList.view.loaded as Map<string, TreeNode[]>;
    const hasLoadedChildren = dataSource.has(parentNodeId);

    if (!hasLoadedChildren) {
      const parentNode = this.treeList.view.data.find(
        treeNode => treeNode.data.id === parentNodeId
      );
      parentNode.data.hasChildren = true;
      dataSource.set(parentNodeId, [node]);
    } else {
      const parentNodeChildren = dataSource.get(parentNodeId);
      if (Array.isArray(parentNodeChildren)) {
        parentNodeChildren.push(node);
      }
    }

    this.treeList.updateView();
  }

  private removeNodeFromParent(nodeId, parentNodeId) {
    const dataSource = this.treeList.view.loaded as Map<string, TreeNode[]>;
    const parentNodeChildren = dataSource.get(parentNodeId);
    const targetNodeIndex = parentNodeChildren.findIndex(node => node.id === nodeId);

    if (targetNodeIndex === parentNodeChildren.length - 1) {
      parentNodeChildren.pop();
    } else {
      parentNodeChildren.splice(targetNodeIndex, 1);
      dataSource.set(parentNodeId, parentNodeChildren);
    }

    this.treeList.updateView();
  }

  private selectLastChildFromParentNode(parentNodeId) {
    const childrenNodes = this.treeList.view.data.filter(
      node => node.data.parentId === parentNodeId
    );

    return childrenNodes[childrenNodes.length - 1];
  }

  private isChildrenLoaded(parentId): boolean {
    const sourceData = this.treeList.view.loaded as Map<string, TreeNode>;
    return sourceData.has(parentId);
  }

  private showMaxChildLoadedWarning(
    rootId: string,
    parentId: string,
    allChildrenLength: number
  ): void {
    if (rootId !== parentId) {
      /** For root nodes we have pagination */
      if (allChildrenLength > constants.pagination.maxLoadedChildren) {
        const selectedNode = this.getSelectedData();
        const dialogRef = this.dialogService.getNodeLimitDialog(selectedNode);

        dialogRef
          .afterClosed()
          .pipe(filter(Boolean))
          .subscribe(() => this.makeNodeRoot.emit({id: parentId}));
      }
    }
  }

  private checkAllNodesNumberInDOM(extraNodesLength?: number): void {
    const extraNodes = extraNodesLength || 0;
    const treeListDataLength = this.treeList.view.totalRows + extraNodes;

    if (treeListDataLength > constants.pagination.maxDomNodes) {
      this.showMaxNodesDOMWarn.emit({show: true});
    } else {
      this.showMaxNodesDOMWarn.emit({show: false});
    }
  }

  private checkAndAddNodeFromNextPageAfterDelete() {
    /** If we need to load one more node from next page to replace deleted one */
    if (this.treePaginationService.hasNextPage()) {
      const skip =
        (this.treePaginationService.pageIndex + 1) * this.treePaginationService.nodesPerPage - 1;

      const query = {skip, limit: 1};

      this.nodeService
        .getNodesByParent(this.refModel._id, this.rootData._id, query)
        .pipe(filter(res => res && res.nodes && res.nodes.length))
        .subscribe(res => {
          const node = this.nodeFormatterService.formatNode(res.nodes[0]);
          this.addNewNode(this.rootData._id, node);
        });
    }
  }

  private getSelectedData() {
    const nodeData = this.treeList.view.data.find(item => item.selected)?.data;
    return {...nodeData};
  }

  private getNodeAndParentFromTree(nodeId: string) {
    const dataItem = _.cloneDeep(this.treeList.view.data.find(node => node.id === nodeId));
    const parentItem = _.cloneDeep(
      this.treeList.view.data.find(node => node.id === dataItem?.data?.parentId)
    );

    return [dataItem, parentItem];
  }

  private updateTagsColumnWidth(nodes) {
    this.ngZone.onStable
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        const treeListMaxTagsNumber = this.calculateMaxTagsInNodes(this.treeList.view.data);
        const updatedNodesMaxTagsNumber = this.calculateMaxTagsInNodes(nodes);

        const oldWidth = this.tagsColumnWidth || 400;
        const tagsColumnWidth =
          updatedNodesMaxTagsNumber > treeListMaxTagsNumber
            ? updatedNodesMaxTagsNumber * AVERAGE_TAG_WIDTH
            : treeListMaxTagsNumber * AVERAGE_TAG_WIDTH;

        if (tagsColumnWidth > oldWidth) {
          this.tagsColumnWidth = tagsColumnWidth;
          this.getTreeListColumns();
          this.updateViewportColumns(tagsColumnWidth);
        }

        this.treeList?.updateView();
      });
  }

  private calculateMaxTagsInNodes(nodes = []) {
    return nodes.reduce((total, node) => {
      const tagsNumber = _.get(node, 'tags', []).length;
      return tagsNumber > total ? tagsNumber : total;
    }, 0);
  }

  // For tags column only for now
  private updateViewportColumns(tagsColumnWidth) {
    this.treeList?.headerColumns.forEach(column => {
      if (column.field === 'tags') {
        column.width = tagsColumnWidth;
        column.minResizableWidth = tagsColumnWidth;
      }
    });
  }
}
